<template>
  <div class="shadow text-left text-gray-500 mb-5 bg-white">
    <div class="p-2">
      <div class="p-2 flex items-center justify-between">
        <span class="font-medium text-gray-700 uppercase text-left">
          Organization overview
        </span>
        <span class="text-xs text-gray-400 normal-case">
          {{ windowLabel }}
        </span>
      </div>

      <div v-if="isLoading" class="p-2">
        <PulseLoader color="#ff5a5f" />
      </div>

      <div v-else-if="!orgs.length" class="p-2 text-sm text-gray-500">
        No organizations in range
      </div>

      <div v-else class="p-2 flex flex-wrap gap-3">
        <button
          v-for="org in orgs"
          :key="org.id"
          type="button"
          :class="[
            'border rounded-md p-3 text-left w-full sm:w-56 focus:outline-none',
            String(selectedOrg) === String(org.id)
              ? 'border-current bg-gray-50'
              : 'border-gray-200 hover:border-gray-300',
          ]"
          @click="
            $emit(
              'select',
              String(selectedOrg) === String(org.id) ? '' : org.id
            )
          "
        >
          <div class="flex items-baseline justify-between">
            <span class="font-medium text-gray-700 truncate" :title="org.label">
              {{ org.label }}
            </span>
          </div>

          <dl class="mt-2 text-sm">
            <div class="flex justify-between">
              <dt class="text-gray-600">Jobs</dt>
              <dd class="text-gray-500">{{ formatN(org.total_jobs) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-600">Files</dt>
              <dd class="text-gray-500">{{ formatN(org.total_files) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-600">Queue depth</dt>
              <dd class="text-gray-500">{{ formatN(org.queue_depth) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-600">Records queued</dt>
              <dd class="text-gray-500">{{ formatN(org.records_in_queue) }}</dd>
            </div>
          </dl>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PulseLoader from "vue-spinner/src/PulseLoader";
import humanize from "humanize";

export default {
  name: "JobsOrgSummary",
  components: { PulseLoader },
  props: {
    // Filter-bar params to scope the rollup by. Mirrors what the jobs list
    // sends, minus pagination/ordering.
    params: {
      type: Object,
      default: () => ({}),
    },
    // Currently active organization filter, so the matching card reads as
    // selected. Compared as a string -- the select binds option values as
    // strings while the API returns numeric ids.
    selectedOrg: {
      type: [String, Number],
      default: "",
    },
  },
  emits: ["select", "loaded"],
  data() {
    return {
      orgs: [],
      isLoading: false,
    };
  },
  computed: {
    windowLabel() {
      return this.params.start_date && this.params.end_date
        ? "selected date range"
        : "last 30 days";
    },
  },
  watch: {
    params: {
      deep: true,
      handler() {
        this.fetchSummary();
      },
    },
  },
  mounted() {
    this.fetchSummary();
  },
  methods: {
    formatN(count) {
      return humanize.numberFormat(count || 0, 0);
    },
    async fetchSummary() {
      this.isLoading = true;
      try {
        const response = await this.$store.dispatch("fetching/FETCH", {
          apiCall: () =>
            this.$alfredService.fetchAdminJobsOrgSummary(this.params),
        });
        if (response) {
          // The rollup serializes the values() keys verbatim, so flatten the
          // double-underscore names into something the template can read.
          this.orgs = response.map((row) => {
            const id = row.created_by__organization_id;
            return {
              id,
              label: `Org #${id}`,
              total_jobs: row.total_jobs,
              total_files: row.total_files,
              queue_depth: row.queue_depth,
              records_in_queue: row.records_in_queue,
            };
          });
          // The rollup is the only endpoint that lists organizations, so it
          // also feeds the org filter's options.
          this.$emit("loaded", this.orgs);
        }
      } finally {
        // FETCH rethrows on any response carrying a status, so clear the
        // spinner in a finally -- otherwise a transient 5xx leaves the card
        // grid stuck on the loader.
        this.isLoading = false;
      }
    },
  },
};
</script>
